import axios from 'axios';

/**
 * Represents a HTTP REST client.
 */
class Rest {
  /**
   * Initializes the rest api.
   */
  static init() {
    // Setup axios to pass data each request.
    axios.interceptors.request.use((config) => {
      // EXAMPLE INTERCEPTOR FOR PASSING JWT.
      // const jwt = '';
      // config.headers['Authorization'] = `Bearer ${jwt}`;

      return config;
    });
  }

  /**
   * Issues an HTTP GET request.
   * @param {String} url The url to the api call.
   * @param {Object} config The request configuration.
   * @returns Promise
   */
  static get(url, config) {
    const options = {
      url: url,
      method: 'GET',
      ...config,
    };

    return this._request(options);
  }

  /**
   * Issues an HTTP POST request.
   * @param {String} url The url to the api call.
   * @param {Object} data The data to pass to the api call.
   * @param {Object} config The request configuration.
   * @returns Promise
   */
  static post(url, data, config) {
    const options = {
      url: url,
      method: 'POST',
      data: data || {},
      ...config,
    };

    return this._request(options);
  }

  /**
   * Issues an HTTP PUT request.
   * @param {String} url The url to the api call.
   * @param {Object} data The data to pass to the api call.
   * @param {Object} config The request configuration.
   * @returns Promise
   */
  static put(url, data, config) {
    const options = {
      url: url,
      method: 'PUT',
      data: data || {},
      config: config || {},
    };

    return this._request(options);
  }

  /**
   * Issues an HTTP PATCH request.
   * @param {String} url The url to the api call.
   * @param {Object} data The data to pass to the api call.
   * @param {Object} config The request configuration.
   * @returns Promise
   */
  static patch(url, data, config) {
    const options = {
      url: url,
      method: 'PUT',
      data: data || {},
      config: config || {},
    };

    return this._request(options);
  }

  /**
   * Issues an HTTP DELETE request.
   * @param {String} url The url to the api call.
   * @returns Promise
   */
  static delete(url, config) {
    const options = {
      url: url,
      method: 'DELETE',
      config: config || {},
    };

    return this._request(options);
  }

  /**
   * Expands an array of arguments as parameters to a function.
   * @param {Function} callback The callback to receive the parameters.
   * @returns Promise
   */
  static expandToArgs(callback) {
    return axios.spread(callback);
  }

  static _request(options) {
    return new Promise((resolve, reject) => {
      axios(options)
        .then((res) => {
          resolve({
            data: res.data,
            headers: res.headers,
            statusCode: res.status,
            statusText: res.statusText,
          });
        })
        .catch((error) => {
          if (error.response) {
            // Server received the request and responded with
            // an error outside of the 2xx range (e.g. 4xx, 5xx).
            reject({
              response: {
                data: error.response.data,
                headers: error.response.headers,
                statusCode: error.response.status,
              },
            });
          } else if (error.request) {
            // The request was made, but no response was received.
            reject({ request: error.request });
          } else {
            // There was an issue with setting up the request.
            reject({ message: error.message });
          }
        });
    });
  }
}

export default Rest;
